import { useCallback } from 'react';
import { useAtom } from 'jotai';
import { App } from 'antd';
import {
  Advisor,
  UpdateAdvisorBody,
  updateAdvisorResponseSchema,
} from '@minutes-system/backend/src/api/advisors/schema';
import { advisorQueryAtom } from '../states/state';
import { refreshAllAdvisorsCache } from '../../../../states/statesUtil';
import { guardedClient } from '../../../auth/api/clients';
import fetchAndValidate from '../../../../lib/fetchAndValidate';

interface UseAdvisorDetailReturn {
  advisor: Advisor;
  updateAdvisor: (data: UpdateAdvisorBody) => Promise<{ success: boolean }>;
}

const useAdvisorDetail = (id: number): UseAdvisorDetailReturn => {
  const [advisor, refreshAdvisor] = useAtom(advisorQueryAtom(id));
  const { message } = App.useApp();

  const updateAdvisor = useCallback(
    async (data: UpdateAdvisorBody): Promise<{ success: boolean }> => {
      const response = await guardedClient.advisors[':id'].$put({
        param: { id: String(id) },
        json: data,
      });
      const result = await fetchAndValidate(Promise.resolve(response), updateAdvisorResponseSchema);

      if (result.resultType === 'error') {
        const reasonMessages: Record<typeof result.reason, string> = {
          'exact-duplicate': '同一の営業担当者が既に存在します',
          'name-changed': '姓が変更された可能性のある営業担当者が既に存在します',
        };
        message.error(reasonMessages[result.reason]);
        return { success: false };
      }

      if (result.resultType === 'success-with-warning') {
        const warningMessages: Record<typeof result.reason, string> = {
          'same-name-exists': '同姓同名の営業担当者が存在します',
        };
        message.warning(warningMessages[result.reason]);
      }

      refreshAdvisor();
      refreshAllAdvisorsCache();

      return { success: true };
    },
    [id, message, refreshAdvisor]
  );

  return { advisor, updateAdvisor };
};

export default useAdvisorDetail;
